/** Placeholder a path segment carrying an identifier is replaced with. */
const ID_PLACEHOLDER = ':id';

/**
 * Longest route we will produce. The backend rejects anything longer on the
 * assumption it was never templated, so collapsing here keeps a pathological
 * path from being dropped outright.
 */
const MAX_ROUTE_LENGTH = 120;

/** Canonical 8-4-4-4-12 UUID. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** All digits: the legacy Java ids, e.g. /cases/49521. */
const ALL_DIGITS = /^\d+$/;

/**
 * A long unbroken hex run - GUIDs without dashes, and the 12-character request
 * ids the legacy filter mints.
 *
 * Twelve is the floor on purpose. Shorter would start catching real words:
 * "added", "deface" and "decade" are all valid hex, and collapsing a route word
 * into :id merges two distinct routes in the dashboard, which is worse than
 * missing an id.
 */
const LONG_HEX = /^[0-9a-f]{12,}$/i;

/**
 * Turn a request path into the templated route used as a metric label.
 *
 * Log-based metric labels have a time-series ceiling, so the label has to be
 * bounded by the number of real routes rather than the number of records. The
 * raw path travels alongside it as an ordinary log field, so nothing is lost -
 * this is a cardinality measure, not redaction.
 *
 * Accepts a bare path or a full URL, with or without a query string.
 */
export function templateRoute(url: string): string {
  const path = stripToPath(url);

  const templated = path
    .split('/')
    .filter((segment) => segment !== '')
    .map((segment) => (looksLikeId(segment) ? ID_PLACEHOLDER : segment));

  const route = '/' + templated.join('/');
  if (route.length <= MAX_ROUTE_LENGTH) {
    return route;
  }

  // Truncating mid-segment would invent routes that do not exist, so cut on a
  // boundary and mark it. A route this long is already pathological.
  return route.slice(0, MAX_ROUTE_LENGTH).replace(/\/[^/]*$/, '') + '/…';
}

/**
 * Reduce a URL to its path.
 *
 * Absolute URLs arrive because axios records the resolved request URL. The
 * query string goes because it can hold free text a user typed into a search
 * box, which is unbounded in a way a path id is not.
 */
function stripToPath(url: string): string {
  const withoutQuery = url.split('?')[0].split('#')[0];

  const schemeEnd = withoutQuery.indexOf('://');
  if (schemeEnd === -1) {
    return withoutQuery;
  }

  const afterHost = withoutQuery.indexOf('/', schemeEnd + 3);
  return afterHost === -1 ? '/' : withoutQuery.slice(afterHost);
}

/** An API version segment such as "v1" or "v2". The one digit-bearing word we keep. */
const VERSION_SEGMENT = /^v\d+$/;

/**
 * Turn a request path into a route for the pre-login error report.
 *
 * Stricter than `templateRoute`, because that report goes out with no login and
 * a path can hold a health card number (the public /covid page puts one in the
 * path, e.g. "1234567890KT" or "SMIJ12345678"). So here ANY segment with a
 * digit becomes ":id", except an exact version segment like "v1". Reporting a
 * real word with a digit in it as ":id" is the safe mistake.
 */
export function templateErrorRoute(url: string): string {
  return templateRoute(url)
    .split('/')
    .map((segment) =>
      /\d/.test(segment) && !VERSION_SEGMENT.test(segment) ? ID_PLACEHOLDER : segment,
    )
    .join('/');
}

/** Whether a single path segment carries an identifier rather than a name. */
function looksLikeId(segment: string): boolean {
  return UUID.test(segment) || ALL_DIGITS.test(segment) || LONG_HEX.test(segment);
}
